import React, { useState, useEffect } from 'react';
import { Text, Group, Stack, Select, Switch, Checkbox, Button, Divider, Paper } from '@mantine/core';
import { IconDeviceFloppy, IconRotateClockwise } from '@tabler/icons-react';
import { DeepExcelSettings } from '../../api/deepPresetsClient';

interface ExcelStyleSettingsDrawerProps {
  settings?: DeepExcelSettings | null;
  periodDays?: number | null;
  loading?: boolean;
  onSave?: (settings: DeepExcelSettings) => void | Promise<void>;
}

const ExcelStyleSettingsDrawer: React.FC<ExcelStyleSettingsDrawerProps> = ({ 
  settings,
  periodDays,
  loading = false,
  onSave
}) => {
  const [localSettings, setLocalSettings] = useState<DeepExcelSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Опции для сортировки
  const sortingOptions = [
    { value: 'ROI', label: '📈 ROI' },
    { value: 'WINRATE', label: '🎯 Winrate' },
    { value: 'TRADED_MINTS', label: '💰 Traded Mints' },
    { value: 'LAST_SWAP', label: '⏰ Last Swap' },
  ];

  const detailedSortingOptions = [
    { value: 'ROI', label: '📈 ROI' },
    { value: 'LAST_SWAP', label: '⏰ Last Swap' },
    { value: 'FIRST_SWAP', label: '⏰ First Swap' },
  ];

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        ...settings,
        show_only_period_range_tokens: settings.show_only_period_range_tokens ?? false,
      });
      setHasChanges(false);
      setSaveError(null);
    }
  }, [settings]);

  const handleSettingChange = <K extends keyof DeepExcelSettings>(
    key: K,
    value: DeepExcelSettings[K]
  ) => {
    if (!localSettings) return;
    
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (localSettings && onSave) {
      setSaving(true);
      setSaveError(null);
      try {
        await onSave(localSettings);
        setHasChanges(false);
      } catch (error) {
        setSaveError('Could not save Excel settings. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleReset = () => {
    if (settings) {
      setLocalSettings({
        ...settings,
        show_only_period_range_tokens: settings.show_only_period_range_tokens ?? false,
      });
      setHasChanges(false);
      setSaveError(null);
    }
  };

  const periodLabel = periodDays == null
    ? 'Period Days: Max'
    : `${periodDays}-day window`;
  const periodStartPercent = periodDays == null ? 0 : 28;
  const latestSwapPercent = 78;
  const tokenMarks = [
    { position: 8, address: 'Aj3…' },
    { position: 17, address: '9Ks…' },
    { position: 38, address: 'F4p…' },
    { position: 49, address: 'Dq8…' },
    { position: 60, address: '7Lm…' },
    { position: 69, address: 'Bx2…' },
  ];
  const latestTokenAddress = 'K7v…';

  if (loading) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: 200 }}>
        <Text>Loading Excel settings...</Text>
      </Stack>
    );
  }

  if (!localSettings) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: 200 }}>
        <Text c="dimmed">No settings available</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Text size="xl" fw={700} style={{ color: '#4F46E5' }}>
          📊 Excel Export Settings
        </Text>
      </Group>
      
      <Text size="sm" c="dimmed">
        Configure how your Excel export will be formatted and sorted.
      </Text>

      {/* Summary Wallets Section */}
      <Paper p="md" style={{ backgroundColor: '#161B22', border: '1px solid #374151' }}>
        <Stack gap="md">
          <Text size="lg" fw={600} style={{ color: '#F9FAFB' }}>
            📋 Summary Wallets
          </Text>
          
          <Select
            label="Sort Column"
            description="Choose which column to sort by in the summary"
            value={localSettings.summary_wallets_order_column}
            onChange={(value) => {
              if (value) {
                handleSettingChange(
                  'summary_wallets_order_column',
                  value as DeepExcelSettings['summary_wallets_order_column']
                );
              }
            }}
            data={sortingOptions}
            styles={{
              label: { color: '#F9FAFB', fontWeight: 500 },
              description: { color: '#9CA3AF' },
              input: { backgroundColor: '#0D1117', border: '1px solid #374151', color: '#F9FAFB' },
              dropdown: { backgroundColor: '#0D1117', border: '1px solid #374151' },
              option: { 
                color: '#F9FAFB',
                '&[data-selected]': { backgroundColor: '#374151', color: '#F9FAFB' },
                '&:hover': { backgroundColor: '#21262D' }
              }
            }}
          />
          
          <Switch
            label="Descending Order"
            description="Sort from highest to lowest values"
            checked={localSettings.summary_order_desc}
            onChange={(event) => handleSettingChange('summary_order_desc', event.currentTarget.checked)}
            color="green"
            styles={{
              label: { color: '#F9FAFB' },
              description: { color: '#9CA3AF' }
            }}
          />

        </Stack>
      </Paper>

      {/* Detailed Tokens Section */}
      <Paper p="md" style={{ backgroundColor: '#161B22', border: '1px solid #374151' }}>
        <Stack gap="md">
          <Text size="lg" fw={600} style={{ color: '#F9FAFB' }}>
            🔍 Detailed Tokens
          </Text>
          
          <Select
            label="Sort Column"
            description="Choose which column to sort by in the detailed view"
            value={localSettings.detailed_tokens_order_column}
            onChange={(value) => {
              if (value) {
                handleSettingChange(
                  'detailed_tokens_order_column',
                  value as DeepExcelSettings['detailed_tokens_order_column']
                );
              }
            }}
            data={detailedSortingOptions}
            styles={{
              label: { color: '#F9FAFB', fontWeight: 500 },
              description: { color: '#9CA3AF' },
              input: { backgroundColor: '#0D1117', border: '1px solid #374151', color: '#F9FAFB' },
              dropdown: { backgroundColor: '#0D1117', border: '1px solid #374151' },
              option: { 
                color: '#F9FAFB',
                '&[data-selected]': { backgroundColor: '#374151', color: '#F9FAFB' },
                '&:hover': { backgroundColor: '#21262D' }
              }
            }}
          />
          
          <Switch
            label="Descending Order"
            description="Sort from highest to lowest values"
            checked={localSettings.detailed_order_desc}
            onChange={(event) => handleSettingChange('detailed_order_desc', event.currentTarget.checked)}
            color="green"
            styles={{
              label: { color: '#F9FAFB' },
              description: { color: '#9CA3AF' }
            }}
          />

          <Paper
            p="md"
            radius="md"
            style={{
              background: localSettings.show_only_period_range_tokens
                ? 'linear-gradient(135deg, rgba(79, 70, 229, 0.18) 0%, rgba(16, 185, 129, 0.08) 100%)'
                : '#0D1117',
              border: localSettings.show_only_period_range_tokens
                ? '1px solid rgba(99, 102, 241, 0.8)'
                : '1px solid #374151',
              transition: 'background 160ms ease, border-color 160ms ease',
            }}
          >
            <Stack gap="md">
              <Checkbox
                label="Only tokens in the activity period"
                description={
                  periodDays == null
                    ? 'Period Days is Max, so the window currently covers all available history.'
                    : `For each wallet, export only tokens from the ${periodDays} days leading up to its latest token swap.`
                }
                checked={localSettings.show_only_period_range_tokens}
                onChange={(event) => handleSettingChange(
                  'show_only_period_range_tokens',
                  event.currentTarget.checked
                )}
                color="indigo"
                size="md"
                styles={{
                  label: { color: '#F9FAFB', fontWeight: 600, cursor: 'pointer' },
                  description: { color: '#9CA3AF', lineHeight: 1.45 },
                  input: {
                    cursor: 'pointer',
                    backgroundColor: localSettings.show_only_period_range_tokens ? '#6366F1' : '#111827',
                    borderColor: localSettings.show_only_period_range_tokens ? '#818CF8' : '#4B5563',
                  },
                  icon: { color: '#F8FAFC' },
                }}
              />

              <div
                role="img"
                aria-label={`Token activity window: latest swap minus period days through latest swap. ${periodLabel}.`}
                style={{
                  backgroundColor: 'rgba(7, 9, 14, 0.72)',
                  border: '1px solid #2D3748',
                  borderRadius: 10,
                  padding: '12px 14px 10px',
                }}
              >
                <Group justify="space-between" gap="xs" mb={8} wrap="wrap">
                  <Text size="xs" fw={600} c="#CBD5E1">Token activity example</Text>
                  <Group gap={12} wrap="nowrap">
                    <Group gap={5} wrap="nowrap">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4B5563' }} />
                      <Text size="10px" c="#9CA3AF">excluded</Text>
                    </Group>
                    <Group gap={5} wrap="nowrap">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#A5B4FC' }} />
                      <Text size="10px" c="#C7D2FE">exported</Text>
                    </Group>
                  </Group>
                </Group>

                <div style={{ position: 'relative', height: 90, margin: '0 7px' }}>
                  <Text
                    component="span"
                    size="9px"
                    c="#6B7280"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: `${latestSwapPercent}%`,
                      right: 0,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    no tokens
                  </Text>

                  {/* Full timeline ends at Now. */}
                  <div style={{
                    position: 'absolute',
                    top: 27,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: '#4B5563',
                  }} />

                  {/* The period range is the only highlighted part of the axis. */}
                  <div style={{
                    position: 'absolute',
                    top: 26,
                    left: `${periodStartPercent}%`,
                    width: `${latestSwapPercent - periodStartPercent}%`,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#818CF8',
                  }} />

                  {/* Period boundary. */}
                  <div style={{
                    position: 'absolute',
                    top: 19,
                    left: `${periodStartPercent}%`,
                    width: 2,
                    height: 18,
                    backgroundColor: '#818CF8',
                  }} />

                  {/* Each point represents a token. */}
                  {tokenMarks.map(({ position, address }) => {
                    const isInPeriod = position >= periodStartPercent;

                    return (
                      <React.Fragment key={position}>
                        <div
                          style={{
                            position: 'absolute',
                            top: 22,
                            left: `${position}%`,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: isInPeriod ? '#A5B4FC' : '#4B5563',
                            transform: 'translateX(-50%)',
                          }}
                        />
                        <Text
                          component="span"
                          size="8px"
                          c={isInPeriod ? '#7C8497' : '#5D6472'}
                          style={{
                            position: 'absolute',
                            top: 35,
                            left: `${position}%`,
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {address}
                        </Text>
                      </React.Fragment>
                    );
                  })}

                  {/* Latest token is the anchor for the period window. */}
                  <div style={{
                    position: 'absolute',
                    top: 21,
                    left: `${latestSwapPercent}%`,
                    width: 13,
                    height: 13,
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    transform: 'translateX(-50%)',
                  }} />
                  <Text
                    component="span"
                    size="8px"
                    c="#648578"
                    style={{
                      position: 'absolute',
                      top: 35,
                      left: `${latestSwapPercent}%`,
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {latestTokenAddress}
                  </Text>

                  {/* Now is the end of the axis, after the empty gray gap. */}
                  <div style={{
                    position: 'absolute',
                    top: 21,
                    right: 0,
                    width: 2,
                    height: 14,
                    backgroundColor: '#6B7280',
                  }} />

                  <Text
                    component="span"
                    size="9px"
                    c="#A5B4FC"
                    style={{
                      position: 'absolute',
                      top: 57,
                      left: `${periodStartPercent}%`,
                      width: periodStartPercent === 0 ? 95 : 132,
                      transform: periodStartPercent === 0 ? undefined : 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {periodStartPercent === 0 ? 'all available history' : 'last swap − period_days'}
                  </Text>
                  <Text
                    component="span"
                    size="9px"
                    fw={600}
                    c="#6EE7B7"
                    style={{
                      position: 'absolute',
                      top: 57,
                      left: `${latestSwapPercent}%`,
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Last swap
                  </Text>
                  <Text
                    component="span"
                    size="9px"
                    c="#9CA3AF"
                    style={{ position: 'absolute', top: 57, right: 0 }}
                  >
                    Now
                  </Text>
                </div>

                <Text size="10px" c="#94A3B8" ta="center">
                  Highlighted range: {periodLabel}
                </Text>
              </div>

              <Text size="xs" c="dimmed">
                This affects detailed token sheets only. Summary wallet rows are unchanged.
              </Text>
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      {/* Display Options */}
      <Paper p="md" style={{ backgroundColor: '#161B22', border: '1px solid #374151' }}>
        <Stack gap="md">
          <Text size="lg" fw={600} style={{ color: '#F9FAFB' }}>
            🎨 Display Options
          </Text>
          
          <Switch
            label="Lite Mode"
            description="Enable simplified Excel layout with out MCAP, Profit distribution tables"
            checked={localSettings.lite_mode}
            onChange={(event) => handleSettingChange('lite_mode', event.currentTarget.checked)}
            color="green"
            styles={{
              label: { color: '#F9FAFB' },
              description: { color: '#9CA3AF' }
            }}
          />
        </Stack>
      </Paper>

      <Divider style={{ borderColor: '#374151' }} />

      {saveError && (
        <Text size="sm" c="red.4">
          {saveError}
        </Text>
      )}

      {/* Action Buttons */}
      <Group justify="space-between">
        <Button
          variant="outline"
          leftSection={<IconRotateClockwise size={16} />}
          onClick={handleReset}
          disabled={!hasChanges || saving}
          style={{
            borderColor: '#6B7280',
            color: '#9CA3AF'
          }}
        >
          Reset Changes
        </Button>
        
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={handleSave}
          disabled={!hasChanges || saving}
          loading={saving}
          style={{
            background: hasChanges 
              ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
              : '#374151',
            border: 'none'
          }}
        >
          Save Settings
        </Button>
      </Group>
    </Stack>
  );
};

export default ExcelStyleSettingsDrawer;
